import { designTokens } from '@app/theme';
import { formatDateTime } from '@shared/lib/format-date';
import { Alert, Card, Empty, Tag, Tooltip, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AuditEventResponse } from '@api/generated/types.gen';

import { shortId } from '../api';
import { KEY_ACTIONS, collectEntityTypes, shortHash, verifyAuditChains } from '../lib';

const { Text } = Typography;

type AuditTrailProps = {
  events: AuditEventResponse[];
  actorPhones: Record<string, string>;
};

/**
 * Блок «Лента аудита»: хронология событий договора и связанных сущностей
 * (документы, платежи, передачи, график, споры).
 *
 * Системные события (`actorId = null` — таймауты, авто-подтверждения)
 * подписываются «система». Ключевые для разбора споров действия
 * (`KEY_ACTIONS`) подсвечены. Чипы над лентой фильтруют по типу сущности.
 *
 * Каждая запись несёт `prevHash`/`hash` (append-only hash-chain, отдельная
 * цепочка на каждую пару `entityType + entityId`). В строке показывается
 * сокращённый хеш с копированием полного; связность проверяется на клиенте
 * (`verifyAuditChains`, по всем событиям, не только видимым): точка разрыва
 * помечается красным тегом, над лентой — предупреждение.
 */
export function AuditTrail({ events, actorPhones }: AuditTrailProps) {
  const { t } = useTranslation('agreements');
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  const entityTypes = useMemo(() => collectEntityTypes(events), [events]);
  const chainCheck = useMemo(() => verifyAuditChains(events), [events]);
  const annotated = useMemo(
    () => events.map((event, index) => ({ event, broken: chainCheck.brokenIndexes.has(index) })),
    [events, chainCheck],
  );
  const visible = typeFilter
    ? annotated.filter(({ event }) => event.entityType === typeFilter)
    : annotated;

  const entityLabel = (type: string): string =>
    t(`audit.entityTypes.${type}`, { defaultValue: type });

  return (
    <Card
      title={t('audit.title')}
      style={{ borderColor: designTokens.slate200 }}
      styles={{
        header: { borderBottomColor: designTokens.slate200, fontWeight: 600 },
        body: { padding: 0 },
      }}
    >
      {!chainCheck.valid && (
        <Alert
          type="error"
          showIcon
          banner
          message={t('audit.integrityBroken')}
          description={t('audit.integrityBrokenHint')}
          style={{ borderBlockEnd: `1px solid ${designTokens.slate100}` }}
        />
      )}
      {entityTypes.length > 1 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            padding: '10px 16px',
            borderBlockEnd: `1px solid ${designTokens.slate100}`,
          }}
        >
          <Tag.CheckableTag
            checked={typeFilter === null}
            onChange={() => {
              setTypeFilter(null);
            }}
          >
            {t('audit.filterAll')}
          </Tag.CheckableTag>
          {entityTypes.map((type) => (
            <Tag.CheckableTag
              key={type}
              checked={typeFilter === type}
              onChange={(checked) => {
                setTypeFilter(checked ? type : null);
              }}
            >
              {entityLabel(type)}
            </Tag.CheckableTag>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <div style={{ padding: 32 }}>
          <Empty description={t('audit.empty')} />
        </div>
      ) : (
        visible.map(({ event: e, broken }, idx) => {
          const isKey = KEY_ACTIONS.has(e.action ?? '');
          const actor = e.actorId
            ? (actorPhones[e.actorId] ?? shortId(e.actorId))
            : t('audit.system');
          return (
            <div
              key={e.id ?? idx}
              style={{
                padding: '8px 16px',
                borderBlockStart: idx > 0 ? `1px solid ${designTokens.slate100}` : 'none',
                background: broken ? '#FEF2F2' : isKey ? '#FFFBEB' : undefined,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '2px 8px',
                }}
              >
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    fontFamily: '"JetBrains Mono", monospace',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatDateTime(e.createdAt)}
                </Text>
                <Tag style={{ fontSize: 11, marginInlineEnd: 0 }}>
                  {entityLabel(e.entityType ?? '')}
                </Tag>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: isKey ? 700 : 500,
                    fontFamily: '"JetBrains Mono", monospace',
                    color: isKey ? designTokens.amber600 : designTokens.navy900,
                  }}
                >
                  {e.action}
                </Text>
                <Text
                  style={{
                    fontSize: 12,
                    fontFamily: '"JetBrains Mono", monospace',
                    color: e.actorId ? designTokens.navy700 : designTokens.slate400,
                  }}
                >
                  {actor}
                </Text>
                {e.prevStatus != null && e.newStatus != null && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {e.prevStatus} → {e.newStatus}
                  </Text>
                )}
                {e.prevStatus == null && e.newStatus != null && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    → {e.newStatus}
                  </Text>
                )}
                {e.hash && (
                  <Tooltip
                    title={
                      <span style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11 }}>
                        {t('audit.prevHash')}: {e.prevHash ?? t('audit.chainStart')}
                      </span>
                    }
                  >
                    <Text
                      copyable={{ text: e.hash, tooltips: false }}
                      style={{
                        fontSize: 12,
                        fontFamily: '"JetBrains Mono", monospace',
                        color: designTokens.slate500,
                        marginInlineStart: 'auto',
                      }}
                    >
                      {shortHash(e.hash)}
                    </Text>
                  </Tooltip>
                )}
                {broken && (
                  <Tag color="error" style={{ fontSize: 11, marginInlineEnd: 0 }}>
                    {t('audit.chainBroken')}
                  </Tag>
                )}
              </div>
              {(e.reason ?? e.ip) && (
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '2px 12px',
                    marginBlockStart: 2,
                  }}
                >
                  {e.reason && (
                    <Text style={{ fontSize: 12, color: designTokens.red600 }}>
                      {t('audit.reason', { reason: e.reason })}
                    </Text>
                  )}
                  {e.ip && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      IP {e.ip}
                    </Text>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
    </Card>
  );
}
